import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _retryCount?: number;
  }
  export interface InternalAxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _retryCount?: number;
  }
}

export interface CustomAxiosError<T = unknown, D = unknown> extends AxiosError<T, D> {
  userMessage?: string;
}

// Use VITE_API_URL if set, otherwise use relative path
// Relative path /api is routed by ingress to api-backend
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  }
  // Use relative path - works with ingress routing /api to api-backend
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('API Client initialized with baseURL:', api.defaults.baseURL);

// Request interceptor - add JWT token if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Try to get token from localStorage (if stored there)
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: CustomAxiosError) => {
    const requestUrl = error.config?.url || '';
    const bypass401Redirect =
      requestUrl.includes('/investor/access/verify') || requestUrl.includes('/investor/access/set');
    
    // Check if this request should skip auth redirect (e.g., during initial auth check)
    const skipAuthRedirect = error.config?.skipAuthRedirect || false;
    
    // Check if this is an auth check request - don't redirect for these
    const isAuthCheckRequest = requestUrl === '/auth/profile' || requestUrl.includes('/auth/profile');

    // Handle network errors (no response from server)
    if (!error.response) {
      // Check if it's a network error
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        console.error('Network Error: Cannot connect to backend server');
        console.error('Backend URL:', error.config?.baseURL);
        console.error('Request URL:', error.config?.url);
        // Provide a more helpful error message
        error.userMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('Request timeout: Server took too long to respond');
        error.userMessage = 'Request timeout. Please try again.';
      } else {
        console.error('Unknown network error:', error.message);
        error.userMessage = error.message || 'Network error occurred';
      }
    }

    if (error.response?.status === 401 && !bypass401Redirect && !skipAuthRedirect && !isAuthCheckRequest) {
      console.log('🔐 API Client: Received 401 Unauthorized');
      console.log('🔐 Current path:', window.location.pathname);
      console.log('🔐 Request URL:', error.config?.url);
      console.log('🔐 Request method:', error.config?.method);
      
      // Prevent infinite retry loops - if this request has already been retried, don't retry again
      if ((error.config?._retryCount ?? 0) >= 1) {
        console.log('🔐 Request already retried, preventing infinite loop');
        return Promise.reject(error);
      }
      
      // Mark this request as retried
      if (error.config) {
        error.config._retryCount = (error.config._retryCount || 0) + 1;
      }
      
      // Try to refresh the token first (but don't refresh the refresh endpoint itself)
      if (error.config?.url !== '/auth/refresh') {
        try {
          console.log('🔐 Attempting to refresh JWT token...');
          // Refresh endpoint is GET, not POST
          await api.get('/auth/refresh', {
            headers: {
              ...error.config?.headers
            }
          });
          console.log('🔐 Token refreshed successfully, retrying original request...');

          // Retry the original request
          const originalRequest = error.config;
          if (!originalRequest) {
            return Promise.reject(error);
          }
          return api(originalRequest);
        } catch (refreshError: unknown) {
          const refreshAxiosError = refreshError as AxiosError;
          console.log(
            '🔐 Token refresh failed:',
            refreshAxiosError.response?.status,
            refreshAxiosError.message,
          );
          // If refresh failed, reject the error and let the caller handle it
          // Don't immediately redirect - let ProtectedRoute handle it after auth check
          return Promise.reject(error);
        }
      }

      // Don't automatically redirect on 401 - let ProtectedRoute handle navigation
      // This prevents kicking users out during browser back navigation
      console.log('🔐 401 error - letting ProtectedRoute handle navigation');
      
      // Clear any stored user data (but don't redirect)
      localStorage.removeItem('user');
      
      // Return the error so ProtectedRoute can handle it appropriately
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default api;

